import { thunk, computed } from 'easy-peasy'
import base64 from 'base-64'

import { Set, Thunk, Computed } from '../types'
import { set } from '../utils'
import { isUsable } from '../../services/session'

interface SessionUtils {
  // State
  token: string | null
  active: boolean
  claims: Computed<
    this,
    {
      _id: string
      space: string
      admin: boolean
      role?: string
      spaces?: string[]
    }
  >

  // Actions
  setToken: Set<this, 'token'>
  setActive: Set<this, 'active'>

  // Thunk
  connectWithToken: Thunk<this, string>
  disconnect: Thunk<this>
  logout: Thunk<this>
  restore: Thunk<this, never, this['claims']['result']>
}

export interface ModelUtils {
  session: SessionUtils
}

export const modelUtils: ModelUtils = {
  session: {
    // State
    token: null,
    active: true, // Defaults to true to avoid showing the "change password" to recurring admins
    claims: computed((state) => {
      const decoded = JSON.parse(
        base64.decode(state.token?.split('.')[1] ?? '') || '{}',
      )
      return decoded || { _id: '', space: '', admin: false }
    }),

    // Actions
    setToken: set('token'),
    setActive: set('active'),

    // Thunks
    connectWithToken: thunk((actions, token) => {
      localStorage.setItem('@token', token)
      actions.setToken(token)
    }),
    // Déconnexion automatique (jeton expiré, refusé, compte sans droits) : ne
    // concerne que le jeton de CET onglet. Le jeton stocké peut être celui,
    // plus récent, d'un autre onglet qui vient de se connecter ; l'effacer
    // déconnecterait cet autre onglet.
    disconnect: thunk((actions, _, helpers) => {
      if (localStorage.getItem('@token') === helpers.getState().token) {
        localStorage.removeItem('@token')
      }
      actions.setToken(null)
    }),
    // Déconnexion demandée par l'utilisateur : elle vaut pour tous les onglets.
    logout: thunk((actions) => {
      localStorage.removeItem('@token')
      actions.setToken(null)
    }),
    restore: thunk((actions, _, helpers) => {
      const stored = localStorage.getItem('@token')

      // Un jeton stocké expiré — ou dépourvu d'expiration, donc antérieur à la
      // 2FA (#7512) — est purgé : le garder ferait échouer toutes les requêtes
      // sans jamais proposer de se reconnecter.
      let token = isUsable(stored) ? stored : null

      if (stored && !token) {
        localStorage.removeItem('@token')
      }

      // if there is a token in url, we should use it
      // Exception délibérée : c'est le rendu PDF headless, alimenté par des
      // jetons de cron. Il ne passe pas par le login et n'est pas soumis aux
      // règles de session ci-dessus.
      const urlParams = new URLSearchParams(window.location.search)
      const tokenFromUrl = urlParams.get('token')
      if (tokenFromUrl) {
        token = tokenFromUrl
      }

      actions.setToken(token)

      return helpers.getState().claims
    }),
  },
}
